import { FC, useEffect, useState } from "react";
import { Button, Input, Select2 } from "../../../components/ui";
import usePaymentDetails from "../../../hooks/trade_detail/usePaymentDetails";
import PartnerInfo from "../../../components/home/partner";
import { useSelector } from "react-redux";
import { TRootState } from "../../../store";
import SelectDate from "../../../components/ui/select-date";
import moment from "moment";
import { FaXmark } from "react-icons/fa6";
import { numberToWords } from "../../../components/number_to_word_converter/number_converter";
import {
  fixedDigitNumber,
  getCurrencyNumberWithSymbol,
} from "../../../helpers/get-currency-number";
import DocumentPreViewer from "../../../components/commons/document-preview";
import TradeFileUploader from "../../../components/trade-file-uploader/trade_file_upload";
import DragDropUploader from "../../../components/components-library/drag-drop-upload";
import { Tooltip } from "@mui/material";
import { IoInformationCircleOutline } from "react-icons/io5";
import { enableExcessPayment } from "../../../helpers/getText";
import { getBalancePayable } from "../../../helpers/trade";

const RecordPayment: FC<{
  onClose: () => void;
  mode?: "edit";
  refundedTotal?: number;
}> = ({ onClose, mode, refundedTotal = 0 }) => {
  const {
    paymentUpdateStatus,
    paymentHistoryObj,
    tradeDetailsById,
    editAdvPay,
    selectedPaymentType,
    editAdvancePayment,
    // arrivalpmtHistory,
    arrival,
  } = useSelector((state: TRootState) => ({
    paymentUpdateStatus: state.arrivalDetails.paymentUpdateStatus,
    paymentHistoryObj: state.arrivalDetails.paymentHistoryObj,
    tradeDetailsById: state.tradeDetailsById.tradeDetailsbyId,
    editAdvPay: state.arrivalDetails.editAdvancePayment,
    selectedPaymentType: state.paymentDetails.selectedPaymentType,
    editAdvancePayment: state.arrivalDetails.editAdvancePayment,
    arrivalpmtHistory: state.arrivalDetails.arrivalpmtHistory,
    arrival: state.arrivalDetails.arrivalDocs.arrival,
  }));

  const {
    recordPaymentData,
    setRecordPaymentData,
    onRemoveReceiptImage,
    onUploadReceiptImage,
    otherDocuments,
    paymentDocuments,
    billCreationStatus,
    submitRecordPayment,
  } = usePaymentDetails({
    type: selectedPaymentType,
  });

  const onSubmitRecordPayment = () => {
    submitRecordPayment(onClose);
  };
  const [amountOnchangeStatus, setAmountOnchangeStatus] = useState(false);

  const handleDateSelection = (_date: string) => {
    let date = moment(_date).format("YYYY-MM-DD");
    setRecordPaymentData({
      ...recordPaymentData,
      paymentDate: date,
    });
  };

  const handlePaymentReceiptDate = (_date: string) => {
    let date = moment(_date).format("YYYY-MM-DD");
    setRecordPaymentData({
      ...recordPaymentData,
      pmtReceiptDate: date,
    });
  };

  useEffect(() => {
    setRecordPaymentData({
      ...recordPaymentData,
      paymentTypeVal: selectedPaymentType || "",
    });
  }, []);

  useEffect(() => {
    setRecordPaymentData({
      ...recordPaymentData,
      partyId:
        selectedPaymentType == "INWARD"
          ? tradeDetailsById?.destPartyId || ""
          : tradeDetailsById?.srcPartyId || "",
      paymentTypeVal: selectedPaymentType || "",
    });
  }, [selectedPaymentType]);

  useEffect(() => {
    if (mode === "edit") {
      setRecordPaymentData({
        ...recordPaymentData,
        partyId: recordPaymentData?.partyId,
        paymentDate: recordPaymentData?.paymentDate,
        paidAmount: recordPaymentData?.paidAmount,
        paymentType: recordPaymentData?.paymentType,
        paymentMode: recordPaymentData?.paymentMode,
        comments: recordPaymentData?.comments,
        paymentTypeVal: recordPaymentData?.paymentTypeVal,
        requestAdv: recordPaymentData?.requestAdv,
        pmtReceiptDate: recordPaymentData?.pmtReceiptDate,
        desc: recordPaymentData?.desc,
        utr: recordPaymentData?.utr,
        paymentStatus: recordPaymentData?.paymentStatus,
        platformFee: tradeDetailsById?.destPf ?? "",
        delayedPmtCharges: tradeDetailsById?.destDelayCharges ?? "",
      });
    }
  }, []);

  useEffect(() => {
    if (!editAdvancePayment && selectedPaymentType === "OUTWARD") {
      const calculateSrcdAmount =
        Number(tradeDetailsById?.totalReceivable || 0) -
        Number(tradeDetailsById?.srcPf || 0) +
        Number(tradeDetailsById?.srcDis || 0) -
        Number(tradeDetailsById?.outwardPmt || 0);

      if (calculateSrcdAmount > 0) {
        setRecordPaymentData({
          ...recordPaymentData,
          paidAmount: calculateSrcdAmount,
        });
      }
    } else if (!editAdvancePayment && selectedPaymentType === "INWARD") {
      const calculatedDestAmount =
        (Number(tradeDetailsById?.totalPayable) +
          Number(tradeDetailsById?.destDelayCharges) +
          Number(tradeDetailsById?.destPf) -
          Number(tradeDetailsById?.destDis) || 0) -
        (tradeDetailsById?.inwardPmt || 0);

      const roundedAmount = Number(calculatedDestAmount.toFixed(2));

      if (roundedAmount > 0) {
        setRecordPaymentData({
          ...recordPaymentData,
          paidAmount: roundedAmount,
        });
      }
    }
  }, [editAdvancePayment, tradeDetailsById, selectedPaymentType]);

  const _caculateAmount = (recordPaymentData: any) => {
    let finalReceivable =
      Number(tradeDetailsById?.finalPayable) -
      Number(tradeDetailsById?.destPf) +
      recordPaymentData.platformFee;

    let balanceReceivable;
    if (paymentUpdateStatus) {
      balanceReceivable =
        Number(finalReceivable) +
        Number(recordPaymentData.delayedPmtCharges) -
        (tradeDetailsById?.inwardPmt || 0) +
        Number(recordPaymentData.actualAmount);
    } else {
      balanceReceivable =
        Number(finalReceivable) +
        Number(recordPaymentData.delayedPmtCharges) -
        (tradeDetailsById?.inwardPmt || 0);
    }

    let adjustedAmount = balanceReceivable;
    let walletAmount =
      Number(recordPaymentData.paidAmount) - Number(balanceReceivable);

    recordPaymentData = {
      ...recordPaymentData,
      finalReceivable,
      adjustedAmount,
      walletAmount,
    };
    setRecordPaymentData(recordPaymentData);
  };

  const [amountTrigger, setAmountTrigger] = useState(false);

  useEffect(() => {
    if (
      editAdvancePayment === false &&
      recordPaymentData.paymentMode === "WALLET" &&
      !amountTrigger
    ) {
      let balReceivable =
        Number(tradeDetailsById?.finalPayable || 0) +
        Number(tradeDetailsById?.destDelayCharges || 0) -
        (tradeDetailsById?.inwardPmt || 0);

      let walletBalance = Number(tradeDetailsById?.waBal || 0);

      const value =
        balReceivable <= 0
          ? walletBalance
          : walletBalance <= 0
          ? 0
          : balReceivable > walletBalance
          ? walletBalance
          : balReceivable;

      setRecordPaymentData({
        ...recordPaymentData,
        paidAmount: value,
      });
    }
  }, [recordPaymentData?.paymentMode]);

  return (
    <>
      <div
        className="overflow-y-auto py-[18px]"
        style={{
          height: "calc(100vh - 9rem)",
        }}
      >
        <div className="flex gap-2 w-[100%] px-[18px]">
          <div className={"w-[50%]"}>
            <span className="font-semibold fs-14">
              {selectedPaymentType === "INWARD"
                ? "Received From "
                : "Payable To "}
            </span>
            <div className="flex border rounded-md items-center gap-2 px-2 bg-white mt-2 h-[80px]">
              <div className="flex py-2">
                {selectedPaymentType === "INWARD" && (
                  <PartnerInfo
                    partyType="From"
                    name={tradeDetailsById?.destName || ""}
                    tradeName={tradeDetailsById?.destTradeName + "(Dest)" || ""}
                    profilePic={""}
                  />
                )}
                {selectedPaymentType === "OUTWARD" && (
                  <PartnerInfo
                    partyType="To"
                    name={tradeDetailsById?.srcName || ""}
                    tradeName={
                      tradeDetailsById?.srcTradeName + "(Source)" || ""
                    }
                    profilePic={""}
                  />
                )}
              </div>
            </div>
          </div>

          <div
            className={
              "flex border rounded-md items-center gap-2 px-2 bg-white mt-8 w-[50%] h-[80px]"
            }
          >
            {selectedPaymentType === "OUTWARD" && (
              <div className="flex justify-between w-full">
                <div className="flex flex-col w-[48%]">
                  <span className="fs-12 color-sub-text">Paid Till date</span>
                  <span className="fs-14">
                    {getCurrencyNumberWithSymbol(
                      tradeDetailsById?.outwardPmt,
                      true
                    )}
                  </span>
                </div>

                <div className="flex flex-col w-[48%]">
                  <span className="fs-12 color-sub-text">Balance Payable</span>
                  <span className="fs-14">
                    {getCurrencyNumberWithSymbol(
                      getBalancePayable({
                        totalReceivable: tradeDetailsById?.totalReceivable,
                        srcPf: Number(tradeDetailsById?.srcPf),
                        srcDis: Number(tradeDetailsById?.srcDis),
                        outwardPmt: tradeDetailsById?.outwardPmt,
                        refunded: refundedTotal,
                      }),
                      true
                    )}
                  </span>
                </div>
              </div>
            )}

            {selectedPaymentType === "INWARD" && (
              <div className="flex justify-between w-full">
                <div className="flex flex-col w-[48%]">
                  <span className="fs-12 color-sub-text">
                    Received Till date
                  </span>
                  <span className="fs-14">
                    {getCurrencyNumberWithSymbol(
                      tradeDetailsById?.inwardPmt,
                      true
                    )}
                  </span>
                </div>

                <div className="flex gap-1">
                  <div className="flex flex-col">
                    <span className="fs-12 color-sub-text">
                      Balance Receivable
                    </span>
                    <span className="fs-14">
                      {getCurrencyNumberWithSymbol(
                        Number(tradeDetailsById?.finalPayable) +
                          Number(tradeDetailsById?.destDelayCharges) -
                          (tradeDetailsById?.inwardPmt || 0),
                        true
                      )}
                    </span>
                  </div>
                  <div>
                    <Tooltip
                      arrow
                      title={
                        <div className="text-sm p-2">
                          <div>
                            <strong>Balance Receivable: </strong>
                            {getCurrencyNumberWithSymbol(
                              Number(tradeDetailsById?.finalPayable) -
                                Number(tradeDetailsById?.inwardPmt || 0),
                              true
                            )}
                          </div>
                          <div>
                            <strong>Delay Charges: </strong>
                            {getCurrencyNumberWithSymbol(
                              Number(tradeDetailsById?.destDelayCharges),
                              true
                            )}
                          </div>
                          <div>
                            <strong>Pending Receivable: </strong>
                            {getCurrencyNumberWithSymbol(
                              Number(tradeDetailsById?.finalPayable) +
                                Number(tradeDetailsById?.destDelayCharges) -
                                (tradeDetailsById?.inwardPmt || 0),
                              true
                            )}
                          </div>
                        </div>
                      }
                    >
                      <span>
                        <IoInformationCircleOutline
                          className="text-blue-500 cursor-pointer"
                          size={20}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedPaymentType === "OUTWARD" ? (
          <div className="card_space_18">
            <div className="relative w-[100%]">
              <span className="font-semibold fs-14">Select Date</span>
              <SelectDate
                onChange={handleDateSelection}
                selectedDate={recordPaymentData?.paymentDate!}
              />
            </div>

            <div className="w-full flex gap-2 space_18">
              <div className="w-[50%]">
                {recordPaymentData?.paymentType === "ADVANCE" &&
                tradeDetailsById?.reqAdv != null ? (
                  <Input
                    type="number"
                    label={"Amount Paid"}
                    value={recordPaymentData.requestAdv}
                    onChange={(e) =>
                      setRecordPaymentData({
                        ...recordPaymentData,
                        requestAdv: parseFloat(e.target.value),
                        paidAmount: parseFloat(e.target.value),
                      })
                    }
                  />
                ) : (
                  <Input
                    type="number"
                    label="Amount Paid"
                    value={recordPaymentData.paidAmount}
                    onChange={(e) => {
                      setRecordPaymentData({
                        ...recordPaymentData,
                        paidAmount: parseFloat(e.target.value),
                      });
                      setAmountOnchangeStatus(true);
                    }}
                  />
                )}
              </div>

              <div className="w-[50%]">
                <Input
                  label="UTR Number"
                  value={recordPaymentData.utr}
                  onChange={(e) =>
                    setRecordPaymentData({
                      ...recordPaymentData,
                      utr: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className={`relative flex space_18 gap-4`}>
              <Select2
                value={recordPaymentData.paymentType}
                lable="Payment Type"
                paymentMargin={true}
                options={[
                  {
                    label: "Transportation",
                    value: "TRANSPORTATION",
                  },
                  {
                    label: "Advance",
                    value: "ADVANCE",
                    disabled:
                      (tradeDetailsById?.advanceRcvd !== 0 && !editAdvPay) ||
                      tradeDetailsById?.reqAdv?.appStatus !== "APPROVED",
                  },
                  {
                    label: "Partial Payment",
                    value: "PARTIAL_PAYMENT",
                  },
                  {
                    label: "Full Payment",
                    value: "FULL_PAYMENT",
                  },
                  {
                    label: "Final Payment",
                    value: "FINAL_PAYMENT",
                  },
                ]}
                starMark={true}
                onChange={(selectedPaymentType) => {
                  const paymentType = selectedPaymentType.value;

                  let updatedData = {
                    ...recordPaymentData,
                    paymentType: "",
                  };

                  if (paymentType === "ADVANCE") {
                    const userHasNotEnteredAmount =
                      recordPaymentData.paidAmount === undefined ||
                      recordPaymentData.paidAmount === null ||
                      recordPaymentData.paidAmount === "" ||
                      isNaN(Number(recordPaymentData.paidAmount));

                    updatedData.paymentType = "ADVANCE";

                    if (userHasNotEnteredAmount) {
                      const advAmount =
                        Number(tradeDetailsById?.reqAdv?.loanAmt) || "";
                      updatedData.requestAdv = advAmount;
                      updatedData.paidAmount = advAmount;
                    } else {
                      if (paymentType == "ADVANCE" && !amountOnchangeStatus) {
                        const advAmount =
                          Number(tradeDetailsById?.reqAdv?.requestAdv) || "";
                        updatedData.requestAdv = advAmount;
                        updatedData.paidAmount = advAmount;
                      } else {
                        updatedData.requestAdv = Number(
                          recordPaymentData.paidAmount
                        );
                      }
                    }
                  } else if (paymentType === "TRANSPORTATION") {
                    updatedData.paymentType = "TRANSPORTATION";
                  } else if (
                    paymentType === "PARTIAL_PAYMENT" ||
                    paymentType === "INSTALLMENT"
                  ) {
                    updatedData.paymentType = "PARTIAL_PAYMENT";
                  } else if (paymentType === "FINAL_PAYMENT") {
                    updatedData.paymentType = "FINAL_PAYMENT";
                  } else if (paymentType === "FULL_PAYMENT") {
                    updatedData.paymentType = "FULL_PAYMENT";
                  }
                  setRecordPaymentData(updatedData);
                }}
                classNames="w-1/2"
              />

              <Select2
                value={recordPaymentData.paymentMode}
                lable="Payment Mode "
                paymentMargin={true}
                options={[
                  {
                    label: "IMPS",
                    value: "IMPS",
                  },
                  {
                    label: "NEFT",
                    value: "NEFT",
                  },
                  {
                    label: "UPI",
                    value: "UPI",
                  },
                  {
                    label: "RTGS",
                    value: "RTGS",
                  },
                  {
                    label: "CASH",
                    value: "CASH",
                  },
                  {
                    label: "CHEQUE",
                    value: "CHEQUE",
                  },
                ]}
                onChange={(selectedPaymentMode) => {
                  setRecordPaymentData({
                    ...recordPaymentData,
                    paymentMode: selectedPaymentMode.value,
                  });
                }}
                classNames="w-1/2"
              />

              <Select2
                value={recordPaymentData.paymentStatus}
                lable="Payment Status"
                paymentMargin={true}
                options={[
                  {
                    label: "Initiated",
                    value: "INITIATED",
                    textColor: "color-orange",
                  },
                  {
                    label: "Cancelled",
                    value: "CANCELLED",
                    textColor: "color-red",
                  },
                  {
                    label: "Reverted",
                    value: "REVERTED",
                    textColor: "color-blue",
                  },
                  {
                    label: "Failed",
                    value: "FAILED",
                    textColor: "color-red",
                  },
                  {
                    label: "Processed",
                    value: "ACCEPTED",
                    textColor: "color-primary",
                  },
                  ...(selectedPaymentType === "OUTWARD"
                    ? [
                        {
                          label: "Refunded",
                          value: "REFUNDED",
                          textColor: "color-blue",
                        },
                      ]
                    : []),
                ]}
                onChange={(selectedPaymentMode) => {
                  setRecordPaymentData({
                    ...recordPaymentData,
                    paymentStatus: selectedPaymentMode.value,
                  });
                }}
                classNames="w-1/2"
              />
            </div>
          </div>
        ) : (
          <div className="card_space_18">
            <div className="flex gap-2 w-[100%]">
              <div className="flex flex-col w-[50%]">
                <span className="font-semibold fs-14">Select Date</span>
                <SelectDate
                  onChange={handleDateSelection}
                  selectedDate={recordPaymentData?.paymentDate!}
                />
              </div>

              {recordPaymentData.paymentMode === "WALLET" &&
                selectedPaymentType === "INWARD" && (
                  <Input
                    label="Wallet Balance"
                    value={tradeDetailsById?.waBal}
                    disabled={true}
                    className="w-[50%]"
                  />
                )}
            </div>

            {enableExcessPayment(recordPaymentData.paymentType) && (
              <div className="gap-2 flex w-[100%] space_18">
                <Input
                  label="Platform Fee"
                  className="w-[50%]"
                  value={recordPaymentData.platformFee}
                  onChange={(e) => {
                    const platformFee = Number(e.target.value) || 0;
                    let recorPayment = {
                      ...recordPaymentData,
                      platformFee,
                    };
                    setRecordPaymentData(recorPayment);
                    _caculateAmount(recorPayment);
                  }}
                />

                <Input
                  label="Delayed Payment Charges"
                  className="w-[50%]"
                  value={recordPaymentData.delayedPmtCharges}
                  onChange={(e) => {
                    const delayedCharges = Number(e.target.value) || 0;

                    let recorPayment = {
                      ...recordPaymentData,
                      delayedPmtCharges: delayedCharges,
                    };
                    setRecordPaymentData(recorPayment);
                    _caculateAmount(recorPayment);
                  }}
                />
              </div>
            )}

            <div className="w-full flex gap-4 space_18">
              <div className="w-[50%]">
                <Input
                  type="number"
                  label="Final Receivable"
                  value={recordPaymentData?.finalReceivable}
                  disabled={true}
                />
              </div>

              <div className="w-[50%]">
                {recordPaymentData.paymentMode !== "WALLET" ? (
                  <Input
                    type="number"
                    label="Amount Received"
                    required={true}
                    value={recordPaymentData.paidAmount}
                    onChange={(e) => {
                      setAmountTrigger(true);
                      let paidAmount = parseFloat(e.target.value);
                      let adjustedAmount = recordPaymentData.adjustedAmount;
                      let walletAmount = recordPaymentData.walletAmount;
                      let balanceReceivable;
                      if (paymentUpdateStatus) {
                        balanceReceivable = (
                          Number(tradeDetailsById?.finalPayable) +
                          Number(tradeDetailsById?.destDelayCharges) -
                          (tradeDetailsById?.inwardPmt || 0) +
                          Number(recordPaymentData.actualAmount)
                        ).toFixed(2);
                      } else {
                        balanceReceivable = (
                          Number(tradeDetailsById?.finalPayable) +
                          Number(tradeDetailsById?.destDelayCharges) -
                          (tradeDetailsById?.inwardPmt || 0)
                        ).toFixed(2);
                      }
                      if (
                        recordPaymentData.paymentType == "FINAL_PAYMENT" ||
                        recordPaymentData.paymentType == "FULL_PAYMENT"
                      ) {
                        if (
                          paidAmount > 0 &&
                          Number(balanceReceivable) < Number(paidAmount)
                        ) {
                          adjustedAmount = Number(balanceReceivable);
                          walletAmount =
                            Number(paidAmount) - Number(balanceReceivable);
                        } else {
                          adjustedAmount = Number(paidAmount);
                          walletAmount = 0;
                        }
                      }
                      let recordPayment = {
                        ...recordPaymentData,
                        paidAmount: paidAmount,
                        adjustedAmount: adjustedAmount,
                        walletAmount: walletAmount,
                      };
                      setRecordPaymentData(recordPayment);
                    }}
                  />
                ) : (
                  <>
                    <Input
                      type="number"
                      label="Amount Received"
                      required={true}
                      value={
                        recordPaymentData.paidAmount === 0
                          ? ""
                          : recordPaymentData.paidAmount
                      }
                      disabled={tradeDetailsById?.waBal === 0}
                      onChange={(e) => {
                        let value = Number(e.target.value);
                        setAmountTrigger(true);
                        setRecordPaymentData({
                          ...recordPaymentData,
                          paidAmount: value,
                        });
                      }}
                    />
                    {tradeDetailsById?.waBal === 0 && (
                      <span className="color-red fs-14 ml-1">
                        Insufficient Wallet Balance
                      </span>
                    )}
                  </>
                )}
                <p className="fs-12 mt-1 color-primary">
                  {" "}
                  {numberToWords(recordPaymentData?.paidAmount || "")}{" "}
                  {recordPaymentData?.paidAmount ? "Rupees Only" : ""}
                </p>
              </div>
            </div>

            <div className="gap-2 flex w-[100%] space_18">
              <div className="w-[50%]">
                {enableExcessPayment(recordPaymentData.paymentType) && (
                  <Input
                    label="Trade Payment"
                    value={recordPaymentData.adjustedAmount}
                    onChange={(e) => {
                      let adjustedAmount = parseFloat(e.target.value);
                      if (adjustedAmount > 0) {
                        let walletAmount = fixedDigitNumber(
                          Number(recordPaymentData.paidAmount) -
                            Number(adjustedAmount)
                        );
                        setRecordPaymentData({
                          ...recordPaymentData,
                          adjustedAmount: adjustedAmount,
                          walletAmount: walletAmount,
                        });
                      } else {
                        setRecordPaymentData({
                          ...recordPaymentData,
                          adjustedAmount: "",
                          walletAmount: "",
                        });
                      }
                    }}
                  />
                )}
              </div>

              <div className="w-[50%]">
                {enableExcessPayment(recordPaymentData.paymentType) && (
                  <Input
                    label="Moved To Wallet"
                    value={recordPaymentData.walletAmount}
                    disabled={true}
                    onChange={(e) => {}}
                  />
                )}
              </div>
            </div>

            <div
              className={`w-full flex gap-2 ${
                enableExcessPayment(recordPaymentData.paymentType) && "space_18"
              }`}
            >
              <Select2
                value={recordPaymentData.paymentType}
                lable="Payment Type"
                required={true}
                paymentMargin={true}
                options={[
                  {
                    label: "Transportation",
                    value: "TRANSPORTATION",
                  },
                  {
                    label: "Advance",
                    value: "ADVANCE",
                  },
                  {
                    label: "Partial Payment",
                    value: "PARTIAL_PAYMENT",
                  },
                  {
                    label: "Full Payment",
                    value: "FULL_PAYMENT",
                  },
                  {
                    label: "Final Payment",
                    value: "FINAL_PAYMENT",
                  },
                ]}
                onChange={(selectedPaymentType) => {
                  let balanceReceivable;
                  if (paymentUpdateStatus) {
                    balanceReceivable = (
                      Number(tradeDetailsById?.finalPayable) +
                      Number(tradeDetailsById?.destDelayCharges) -
                      (tradeDetailsById?.inwardPmt || 0) +
                      Number(recordPaymentData.actualAmount)
                    ).toFixed(2);
                  } else {
                    balanceReceivable = (
                      Number(tradeDetailsById?.finalPayable) +
                      Number(tradeDetailsById?.destDelayCharges) -
                      (tradeDetailsById?.inwardPmt || 0)
                    ).toFixed(2);
                  }
                  let adjustedAmount = recordPaymentData.adjustedAmount;
                  let walletAmount = recordPaymentData.walletAmount;
                  if (
                    selectedPaymentType.value == "FINAL_PAYMENT" ||
                    selectedPaymentType.value == "FULL_PAYMENT"
                  ) {
                    // if (pendingReceivable < Number(recordPaymentData.paidAmount)) {
                    adjustedAmount = Number(balanceReceivable);
                    walletAmount =
                      Number(recordPaymentData.paidAmount) -
                      Number(balanceReceivable);
                    // }
                  }
                  setRecordPaymentData({
                    ...recordPaymentData,
                    adjustedAmount: adjustedAmount,
                    walletAmount: walletAmount,
                    paymentType:
                      selectedPaymentType.value === "TRANSPORTATION"
                        ? "TRANSPORTATION"
                        : selectedPaymentType.value === "ADVANCE"
                        ? "ADVANCE"
                        : selectedPaymentType.value === "INSTALLMENT" ||
                          selectedPaymentType.value === "PARTIAL_PAYMENT"
                        ? "PARTIAL_PAYMENT"
                        : selectedPaymentType.value === "FINAL_PAYMENT"
                        ? "FINAL_PAYMENT"
                        : selectedPaymentType.value === "FULL_PAYMENT"
                        ? "FULL_PAYMENT"
                        : "",
                  });
                }}
                classNames="w-1/2"
              />

              <Select2
                value={recordPaymentData.paymentMode}
                lable="Payment Mode "
                required={true}
                paymentMargin={true}
                options={[
                  {
                    label: "IMPS",
                    value: "IMPS",
                  },
                  {
                    label: "NEFT",
                    value: "NEFT",
                  },
                  {
                    label: "UPI",
                    value: "UPI",
                  },
                  {
                    label: "RTGS",
                    value: "RTGS",
                  },
                  {
                    label: "CASH",
                    value: "CASH",
                  },
                  {
                    label: "CHEQUE",
                    value: "CHEQUE",
                  },
                  {
                    label: "WALLET",
                    value: "WALLET",
                  },
                ]}
                onChange={(selectedPaymentMode) => {
                  setRecordPaymentData({
                    ...recordPaymentData,
                    paymentMode: selectedPaymentMode.value,
                  });
                }}
                classNames="w-1/2"
              />
            </div>
          </div>
        )}

        <div className="px-[18px]">
          <Input
            type="text"
            label="Comments"
            value={recordPaymentData.comments}
            onChange={(e) =>
              setRecordPaymentData({
                ...recordPaymentData,
                comments: e.target.value,
              })
            }
          />
        </div>

        <div className="w-95%] px-[18px] py-[18px] m-4 border-gray bg-white rounded-lg">
          <div className="w-100">
            <div className="flex justify-between items-center right-0">
              <label className="fs-14">Upload Document</label>
              <TradeFileUploader
                onChange={(e: any) => {
                  if (e.target.files && e.target.files.length > 0) {
                    onUploadReceiptImage(e.target.files);
                  }
                }}
                id="payment"
              />
            </div>
            <div className="flex gap-4 mt_8">
              {otherDocuments.length === 0 &&
                (!paymentHistoryObj?.images ||
                  paymentHistoryObj?.images?.filter((img) => img.status === 1)
                    .length === 0) && (
                  <DragDropUploader
                    onDrop={(acceptedFiles: any) => {
                      if (acceptedFiles.length > 0) {
                        onUploadReceiptImage(acceptedFiles, "OTHER");
                      }
                    }}
                  />
                )}
              <div
                className="flex gap-4 overflow-x-auto"
                style={{ width: "100%" }}
              >
                {otherDocuments !== null &&
                  otherDocuments?.map((file, i) => (
                    <div className="shrink-0 overflow-hidden br-10 doc_card relative border">
                      <a href={file.url} target="_blank">
                        <DocumentPreViewer
                          docType={file.docType}
                          url={file.url}
                        />
                      </a>
                      <span
                        className="absolute right-0 top-0 p-1 rounded-full cursor-pointer"
                        style={{ background: "#ff0000de" }}
                        onClick={() => {
                          onRemoveReceiptImage(i, false);
                        }}
                        key={file.url}
                      >
                        <FaXmark style={{ color: "white" }} />
                      </span>
                    </div>
                  ))}

                {paymentHistoryObj?.images.length != 0 &&
                  paymentHistoryObj?.images
                    .filter((img) => img.status == 1)
                    .map((file, i) => (
                      <div
                        className="shrink-0 overflow-hidden br-10 doc_card relative border"
                        key={file.url}
                      >
                        <a href={file.url} target="_blank">
                          <DocumentPreViewer
                            docType={file.docType}
                            url={file.url}
                          />
                        </a>
                        <span
                          className="absolute right-0 top-0 p-1 rounded-full cursor-pointer"
                          style={{ background: "#ff0000de" }}
                          onClick={() => {
                            onRemoveReceiptImage(i, true, "payment", file.id);
                          }}
                        >
                          <FaXmark style={{ color: "white" }} />
                        </span>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>

        {selectedPaymentType === "INWARD" && (
          <div className="rounded-md m-4 border-gray">
            <div
              className={`py-3 px-4 fs-14 font-semibold`}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                background: "#EBFFF0",
              }}
            >
              Confirm Receipt
            </div>
            <div className="px-4 py-2 bg-white rounded-b-md">
              {" "}
              <div className="flex py-3 gap-4">
                <div className="relative grow-[3]" style={{ width: "50%" }}>
                  <span className="font-semibold fs-14">
                    Payment Receipt Date<span className="color-red">*</span>
                  </span>
                  <SelectDate
                    onChange={handlePaymentReceiptDate}
                    selectedDate={recordPaymentData?.pmtReceiptDate}
                  />
                </div>

                <div className="grow-[3]" style={{ width: "50%" }}>
                  <Input
                    type="text"
                    label={"UTR Number"}
                    required={
                      recordPaymentData.paymentMode === "WALLET" ? false : true
                    }
                    value={recordPaymentData.utr}
                    onChange={(e) =>
                      setRecordPaymentData({
                        ...recordPaymentData,
                        utr: e.target.value,
                      })
                    }
                    inputStyle={{ marginTop: "10px" }}
                  />
                </div>
              </div>
              <div className="mb-3">
                <Input
                  type="text"
                  label="Description"
                  // required={true}
                  value={recordPaymentData?.desc}
                  onChange={(e) => {
                    setRecordPaymentData({
                      ...recordPaymentData,
                      desc: e.target.value,
                    });
                  }}
                  className="w-[100%]"
                />
              </div>
            </div>
          </div>
        )}

        {tradeDetailsById?.tradeStatus !== "CANCEL" &&
          tradeDetailsById?.tradeStatus !== "CLOSED" && (
            <div className="bg-white flex absolute bottom-0 justify-between items-center gap-10 px-6 py-4 w-full border-t">
              <Button variant="gray" onClick={onClose}>
                Cancel
              </Button>

              <Button
                onClick={() => {
                  onSubmitRecordPayment();
                }}
                disabled={billCreationStatus.status === "LOADING"}
              >
                Submit
              </Button>
            </div>
          )}
      </div>
    </>
  );
};
export default RecordPayment;
